import { useEffect, useState } from "preact/hooks";
import { emailTestMode, must, sb } from "../lib/supabase";
import { SMS_CONSENT_TEXT } from "../../../src/shared/sms";
import { Badge, Card, ErrorBox, Loading, daysUntil, fmtDate, formData, useAction, useLoad } from "../lib/ui";
import type { Provider } from "../lib/types";
import { AppearanceCard, PasswordCard } from "./account";
import { DocumentList, uploadDocument } from "./documents";

/** What each BCBA sees: a guided checklist, uploads, and their CAQH reminder. */
export function BcbaPortal({ userId, onSignOut }: { userId: string; onSignOut: () => void }) {
  const [docKey, setDocKey] = useState(0);
  const [openStep, setOpenStep] = useState<string | null>(null);
  const [lastDone, setLastDone] = useState(-1);
  const { busy, run } = useAction();
  const { data, error, reload } = useLoad(async () => {
    const p = must(await sb().from("providers").select("*, organizations(name)").eq("user_id", userId).single()) as Provider & { organizations: { name: string } };
    const [creds, docs, apps] = await Promise.all([
      sb().from("provider_credentials").select("*").eq("provider_id", p.id).order("created_at", { ascending: false }),
      sb().from("provider_documents").select("doc_type, review_status").eq("provider_id", p.id),
      sb().from("applications").select("status, effective_on, payers(name)").eq("provider_id", p.id),
    ]);
    return { p, creds: must(creds) as any[], docs: must(docs) as any[], apps: must(apps) as any[] };
  }, [userId, docKey]);

  if (error) return <main class="portal"><ErrorBox error={error} /></main>;
  if (!data) return <Loading />;
  const { p, creds, docs } = data;
  const has = (t: string) => docs.some((d) => d.doc_type === t && d.review_status !== "rejected");
  const cred = (k: string) => creds.find((c) => c.kind === k);
  const caqh = cred("caqh_attestation");
  const caqhDays = caqh?.expires_on ? daysUntil(caqh.expires_on) : null;

  const steps: Array<{ key: string; title: string; done: boolean; body: preact.ComponentChildren }> = [
    { key: "ids", title: "Your NPI and CAQH ID", done: !!p.npi && !!p.caqh_id, body: (
      <form class="grid3" onSubmit={(e) => {
        const f = formData(e);
        run(async () => { must(await sb().rpc("update_my_profile", { p_phone: f.phone, p_npi: f.npi, p_caqh_id: f.caqh_id })); reload(); }, "Saved");
      }}>
        <label>Individual NPI<input name="npi" pattern="\d{10}" inputMode="numeric" value={p.npi ?? ""} /></label>
        <label>CAQH ProView ID<input name="caqh_id" pattern="\d{5,10}" inputMode="numeric" value={p.caqh_id ?? ""} /></label>
        <label>Mobile phone<input name="phone" value={p.phone ?? ""} /></label>
        <div class="span3 row-end"><button disabled={busy}>Save</button></div>
      </form>) },
    ...p.practice_states.map((st) => ({
      key: `lic_${st}`, title: `${st} Licensed Behavior Analyst license`, done: !!cred(st === "NY" ? "ny_lba" : "nj_lba") && has("license"),
      body: <CredentialStep kind={st === "NY" ? "ny_lba" : "nj_lba"} state={st} docType="license" p={p} onDone={() => setDocKey((k) => k + 1)} existing={cred(st === "NY" ? "ny_lba" : "nj_lba")} />,
    })),
    { key: "bacb", title: "BACB certificate (BCBA)", done: !!cred("bacb_certification") && has("bacb_certificate"),
      body: <CredentialStep kind="bacb_certification" docType="bacb_certificate" p={p} onDone={() => setDocKey((k) => k + 1)} existing={cred("bacb_certification")} /> },
    { key: "mal", title: "Malpractice insurance (declarations page)", done: !!cred("malpractice") && has("malpractice"),
      body: <CredentialStep kind="malpractice" docType="malpractice" p={p} onDone={() => setDocKey((k) => k + 1)} existing={cred("malpractice")} malpractice /> },
    { key: "resume", title: "Resume / CV (with month/year for each job)", done: has("resume"),
      body: <SimpleUpload p={p} docType="resume" onDone={() => setDocKey((k) => k + 1)} /> },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  // Auto-advance to the next incomplete step whenever progress changes; otherwise respect the user's clicks.
  useEffect(() => {
    if (doneCount !== lastDone) {
      setLastDone(doneCount);
      setOpenStep(steps.find((s) => !s.done)?.key ?? null);
    }
  });

  return (
    <main class="portal">
      {emailTestMode() && <div class="banner">Email test mode: every email this app sends goes to the owner's test inbox, not to the real recipient.</div>}
      <header class="row"><h1>{p.organizations.name} — credentialing</h1><span class="spacer" />
        <button class="ghost no-print" onClick={() => print()}>Print my summary</button>
        <button class="ghost no-print" onClick={onSignOut}>Sign out</button></header>
      <p>Hi {p.first_name}! We use this to get you in network with insurance plans as fast as possible.
        You've completed <strong>{doneCount} of {steps.length}</strong> steps.</p>
      <div class="progress"><div style={{ width: `${(doneCount / steps.length) * 100}%` }} /></div>

      {steps.map((s) => (
        <details class="step" open={openStep === s.key}
          onToggle={(e) => {
            const isOpen = (e.currentTarget as HTMLDetailsElement).open;
            if (isOpen !== (openStep === s.key)) setOpenStep(isOpen ? s.key : null);
          }}>
          <summary>{s.done ? "✅" : "⬜"} <strong>{s.title}</strong></summary>
          <div class="step-body">{s.body}</div>
        </details>
      ))}

      <Card title="CAQH ProView attestation — only you can do this">
        <p>Insurance plans pull your information from CAQH. CAQH requires you to re-attest every 120 days,
          and plans can't use a profile that has lapsed. Attesting is your personal statement that the information is accurate, so <strong>we will never attest for you</strong>.</p>
        <ol>
          <li>Open <a href="https://proview.caqh.org" target="_blank" rel="noopener noreferrer">proview.caqh.org</a> and sign in.</li>
          <li>Check that your license, BACB certificate, malpractice and practice locations match the documents you uploaded here.</li>
          <li>Make sure the plans you're applying to are authorized to view your profile.</li>
          <li>Click <em>Attest</em>, then come back and record the date below.</li>
        </ol>
        <p>Status: {caqh ? <>last attested {fmtDate(caqh.issued_on)} — next due {fmtDate(caqh.expires_on)}{" "}
          <span class={`badge ${caqhDays! < 0 ? "bad" : caqhDays! <= 30 ? "warn" : "good"}`}>{caqhDays! < 0 ? "overdue" : `${caqhDays} days left`}</span></> : <Badge value="missing" />}</p>
        <form class="row" onSubmit={(e) => {
          const f = formData(e);
          run(async () => { must(await sb().rpc("record_my_caqh_attestation", { p_attested_on: f.attested_on })); reload(); }, "Thanks! Recorded.");
        }}>
          <label>I attested on<input type="date" name="attested_on" required max={new Date().toISOString().slice(0, 10)} /></label>
          <button disabled={busy}>Record my attestation</button>
        </form>
      </Card>

      <TextReminders p={p} onSaved={reload} />

      <Card title="My credentials at a glance">
        <table><thead><tr><th>Item</th><th>Number</th><th>Expires</th><th>Status</th></tr></thead><tbody>
          {["ny_lba", "nj_lba", "bacb_certification", "malpractice", "caqh_attestation"].map((k) => {
            const c = cred(k);
            if (!c && ((k === "ny_lba" && !p.practice_states.includes("NY")) || (k === "nj_lba" && !p.practice_states.includes("NJ")))) return null;
            const d = c?.expires_on ? daysUntil(c.expires_on) : null;
            return <tr><td>{ITEM_LABEL[k]}</td>
              <td>{c?.number ?? "—"}</td><td>{fmtDate(c?.expires_on)}</td>
              <td>{!c ? <Badge value="missing" /> : d !== null && d < 0 ? <Badge value="expired" /> : d !== null && d <= 30 ? <span class="badge warn">{d} days</span> : c.verified_at ? <span class="badge good">verified</span> : <Badge value="unverified" />}</td></tr>;
          })}
          <tr><td>NPI</td><td>{p.npi ?? "—"}</td><td></td><td>{p.npi ? <span class="badge good">on file</span> : <Badge value="missing" />}</td></tr>
          <tr><td>CAQH ProView ID</td><td>{p.caqh_id ?? "—"}</td><td></td><td>{p.caqh_id ? <span class="badge good">on file</span> : <Badge value="missing" />}</td></tr>
        </tbody></table>
        <p class="muted small print-only">Printed {new Date().toLocaleDateString()} from the {p.organizations.name} credentialing portal.</p>
      </Card>

      {data.apps.length > 0 && <Card title="Your insurance plan status">
        <table><tbody>{data.apps.map((a) => <tr><td>{a.payers?.name}</td><td><Badge value={a.status} /></td><td>{a.effective_on ? `effective ${fmtDate(a.effective_on)}` : ""}</td></tr>)}</tbody></table>
      </Card>}

      <Card title="Your uploaded documents"><DocumentList providerId={p.id} staff={false} isAdmin={false} refreshKey={docKey} /></Card>
      <PasswordCard />
      <AppearanceCard />
      <p class="muted small">Please don't upload anything showing your Social Security number or any client information. We never ask for those here.</p>
    </main>
  );
}

const ITEM_LABEL: Record<string, string> = {
  ny_lba: "NY LBA license", nj_lba: "NJ LBA license", bacb_certification: "BACB certification",
  malpractice: "Malpractice insurance", caqh_attestation: "CAQH attestation (next due)",
};

/** Opt in/out of text reminders. The exact consent wording and time are stored with the choice. */
function TextReminders({ p, onSaved }: { p: Provider; onSaved: () => void }) {
  const { busy, run } = useAction();
  const [agree, setAgree] = useState(false);
  const [phone, setPhone] = useState(p.phone ?? "");
  return (
    <Card title="Text reminders (optional)">
      {p.sms_opt_in ? <>
        <p>✅ You'll get occasional texts at <strong>{p.phone}</strong> when something needs you (CAQH re-attestation, an expiring document, an unfinished step).
          Reply STOP at any time to stop.</p>
        <button class="ghost" disabled={busy} onClick={() => run(async () => {
          must(await sb().rpc("set_my_sms_preference", { p_opt_in: false, p_phone: p.phone, p_consent_text: null })); onSaved();
        }, "Text reminders turned off")}>Turn off text reminders</button>
      </> : <form onSubmit={(e) => {
        e.preventDefault();
        run(async () => { must(await sb().rpc("set_my_sms_preference", { p_opt_in: true, p_phone: phone, p_consent_text: SMS_CONSENT_TEXT })); onSaved(); }, "Text reminders are on");
      }}>
        <p class="small">Prefer a quick text over email? We'll only text you about your own credentialing.{p.sms_opt_out_at ? ` (You turned texts off on ${fmtDate(p.sms_opt_out_at)}.)` : ""}</p>
        <label>Mobile number<input name="phone" type="tel" inputMode="tel" autoComplete="tel" required value={phone} onInput={(e) => setPhone(e.currentTarget.value)} placeholder="(718) 555-0123" /></label>
        <label class="check small"><input type="checkbox" checked={agree} onChange={(e) => setAgree(e.currentTarget.checked)} required />{SMS_CONSENT_TEXT}</label>
        <div class="row-end"><button disabled={busy || !agree}>Turn on text reminders</button></div>
      </form>}
    </Card>
  );
}

function CredentialStep({ kind, state, docType, p, onDone, existing, malpractice }:
  { kind: string; state?: string; docType: string; p: Provider; onDone: () => void; existing?: any; malpractice?: boolean }) {
  const { busy, run } = useAction();
  return (
    <form class="grid3" onSubmit={(e) => {
      const f = formData(e);
      const file = ((e.currentTarget as HTMLFormElement).elements.namedItem("file") as HTMLInputElement).files?.[0];
      run(async () => {
        const id = must(await sb().rpc("submit_my_credential", {
          p_kind: kind, p_state: state ?? null, p_number: f.number, p_issued_on: f.issued_on, p_expires_on: f.expires_on,
          p_coverage_per_claim: f.per_claim ? Number(f.per_claim) : null, p_coverage_aggregate: f.aggregate ? Number(f.aggregate) : null,
        })) as string;
        if (file) await uploadDocument(p.org_id, p.id, file, docType, id);
        onDone();
      }, "Saved — thank you!");
    }}>
      {existing && <p class="span3 muted small">On file: {existing.number ?? ""} {existing.expires_on ? `expires ${fmtDate(existing.expires_on)}` : ""} {existing.verified_at ? "✔ verified" : "(waiting for verification)"}. Submit again only if it changed or was renewed.</p>}
      {!malpractice && <label>{kind === "bacb_certification" ? "Certification #" : "License #"}<input name="number" required /></label>}
      <label>{malpractice ? "Policy start" : "Issued"}<input name="issued_on" type="date" /></label>
      <label>Expires<input name="expires_on" type="date" required /></label>
      {malpractice && <><label>Per-claim limit ($)<input name="per_claim" type="number" min="0" placeholder="1000000" /></label>
        <label>Aggregate limit ($)<input name="aggregate" type="number" min="0" placeholder="3000000" /></label></>}
      <label class="span3">Upload a copy (PDF, JPG or PNG — on a phone you can snap a photo)<input name="file" type="file" accept=".pdf,.jpg,.jpeg,.png" required={!existing} /></label>
      <div class="span3 row-end"><button disabled={busy}>{busy ? "Saving…" : "Save"}</button></div>
    </form>
  );
}

function SimpleUpload({ p, docType, onDone }: { p: Provider; docType: string; onDone: () => void }) {
  const { busy, run } = useAction();
  return (
    <form class="row" onSubmit={(e) => {
      e.preventDefault();
      const file = ((e.currentTarget as HTMLFormElement).elements.namedItem("file") as HTMLInputElement).files?.[0];
      if (file) run(async () => { await uploadDocument(p.org_id, p.id, file, docType); onDone(); }, "Uploaded — thank you!");
    }}>
      <input name="file" type="file" accept=".pdf,.jpg,.jpeg,.png" required />
      <button disabled={busy}>{busy ? "Uploading…" : "Upload"}</button>
    </form>
  );
}
